import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Paper, Button, Grid, Chip, Divider, Menu, MenuItem, Card, CardContent, Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SubjectIcon from '@mui/icons-material/Subject';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function ReferenceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDetails = async () => {
    try {
      const res = await axios.get(`${API_URL}/references/${id}`, { headers });
      setReference(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const handleStatusClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    handleStatusClose();
    try {
      await axios.put(`${API_URL}/references/${id}`, { status: newStatus }, { headers });
      fetchDetails(); // Refresh
    } catch (err) {
      alert("Error updating status");
    }
  };

  if (loading) return <Loading message="Loading Reference Details..." />;
  if (!reference) return <Typography>Reference not found</Typography>;

  const getStatusColor = (status) => {
    switch(status) {
        case 'Approved': return 'success';
        case 'Pending': return 'warning';
        case 'Rejected': return 'error';
        default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/references')}
        sx={{ mb: 3, textTransform: 'none', fontWeight: 'bold' }}
      >
        Back to References
      </Button>

      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 4, 
          overflow: 'hidden', 
          border: '1px solid', 
          borderColor: 'divider',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}
      >
        {/* Header Section */}
        <Box sx={{ 
            bgcolor: 'primary.main', 
            color: 'white', 
            p: 4, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)'
        }}>
            <Box>
                <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 1 }}>
                    Reference Details
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'monospace', my: 1 }}>
                    {reference.ref_number}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip 
                        label={reference.type} 
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} 
                    />
                    <Chip 
                        label={reference.status} 
                        onClick={handleStatusClick}
                        onDelete={handleStatusClick}
                        deleteIcon={<EditIcon sx={{ color: 'white !important' }} />}
                        sx={{ 
                            bgcolor: 'white', 
                            color: 'primary.main', 
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                        }} 
                    />
                </Box>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleStatusClose}
                >
                  <MenuItem onClick={() => handleStatusChange('Pending')}>Pending</MenuItem>
                  <MenuItem onClick={() => handleStatusChange('Approved')}>Approved</MenuItem>
                  <MenuItem onClick={() => handleStatusChange('Rejected')}>Rejected</MenuItem>
                </Menu>
            </Box>
            
            {/* QR Code Box */}
            <Box sx={{ 
                bgcolor: 'white', 
                p: 1.5, 
                borderRadius: 2, 
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                textAlign: 'center'
            }}>
                <img 
                    src={`${API_URL}/references/${id}/qr`} 
                    alt="QR Code" 
                    style={{ width: 100, height: 100, display: 'block' }} 
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', mt: 0.5, display: 'block' }}>
                    SCAN ME
                </Typography>
            </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
                {/* Main Info */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                            <SubjectIcon fontSize="small" />
                            <Typography variant="subtitle2">SUBJECT</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="medium">
                            {reference.subject}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                                <PersonIcon fontSize="small" />
                                <Typography variant="subtitle2">RECIPIENT (TO)</Typography>
                            </Box>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                {reference.recipient_to || '—'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                                <LocationOnIcon fontSize="small" />
                                <Typography variant="subtitle2">ADDRESS</Typography>
                            </Box>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                {reference.address || '—'}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                            <DescriptionIcon fontSize="small" />
                            <Typography variant="subtitle2">DESCRIPTION</Typography>
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                                {reference.description || 'No description provided.'}
                            </Typography>
                        </Paper>
                    </Box>
                </Grid>

                {/* Sidebar Info */}
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: 'grey.50' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: 'text.secondary' }}>
                                Meta Information
                            </Typography>
                            
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: 'text.secondary' }}>
                                    <CalendarTodayIcon fontSize="small" />
                                    <Typography variant="caption" fontWeight="bold">DATE CREATED</Typography>
                                </Box>
                                <Typography variant="body1">
                                    {reference.created_at}
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: 'text.secondary' }}>
                                    <AccountCircleIcon fontSize="small" />
                                    <Typography variant="caption" fontWeight="bold">CREATED BY</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '0.9rem' }}>
                                        {reference.user_name ? reference.user_name.charAt(0).toUpperCase() : 'U'}
                                    </Avatar>
                                    <Typography variant="body1">
                                        {reference.user_name || 'Unknown User'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default ReferenceDetails;
